import React, {useEffect, useRef, useState} from "react";
import {Button, Form, InputGroup, Modal, Overlay, Popover} from "react-bootstrap";
import {
    CORP_TYPE,
    JobDetailType,
    saveJobDescriptionAndChallengeInfoHelper,
    SourceType,
} from "../jobDetailsManagementHelper";
import {FILTER_DEFAULT, FILTER_TYPE, JobFilters} from "../Filters/Filters";
import {JobList} from "../JobList/JobList";

export const CopyModal: React.FC<{
    show: boolean;
    onHide: () => void | undefined;
    corpType: CORP_TYPE;
    jobList: JobDetailType[];
    action: "clone-to" | "clone-from";
    selectedJob: JobDetailType;
    setSelectedJob: (job: JobDetailType | undefined) => void;
    copyTo: JobDetailType | undefined;
    setCopyTo: (job: JobDetailType | undefined) => void;
    reload: () => Promise<void>;
}> = (props) => {
    const {setCopyTo, copyTo} = props;
    const [search, setSearch] = useState<string>("");
    const [filters, setFilters] = useState<FILTER_TYPE>(FILTER_DEFAULT);
    const [isCopying, setCopying] = useState<boolean>(false);

    const [popoverTitle, setPopoverTitle] = useState<string>("");
    const [popoverMsg, setPopoverMsg] = useState<string>("");
    const [showPopover, setShowPopover] = useState<boolean>(false);
    const target = useRef(null);
    const source: SourceType = props.selectedJob.source || "INTERNAL";

    const reset = () => {
        setCopyTo(undefined);
        setFilters(FILTER_DEFAULT);
        setSearch("");
    };

    const handleComplete = () => {
        setShowPopover(true);
        setCopying(false);
        reset();
        setTimeout(() => {
            setShowPopover(false);
            props.onHide();
        }, 2500);
    };

    const handleCloneTo = async () => {
        if (copyTo) {
            setCopying(true);
            try {
                await saveJobDescriptionAndChallengeInfoHelper(
                    props.corpType,
                    copyTo.id,
                    props.selectedJob.description,
                    props.selectedJob.challengeInfo,
                    source,
                );
                await props.reload();
                setPopoverTitle("Success!");
                setPopoverMsg(
                    `You have successfully cloned details from ${props.selectedJob.label} to ${copyTo.label}. Opening the cloned job now...`,
                );
            } catch (e) {
                console.error(e);
                setPopoverTitle("Failed!");
                setPopoverMsg(`Something went wrong. Please try again.`);
            } finally {
                handleComplete();
            }
        }
    };

    const handleCloneFrom = async () => {
        let copyFrom = copyTo;
        if (copyFrom) {
            setCopying(true);
            try {
                await saveJobDescriptionAndChallengeInfoHelper(
                    props.corpType,
                    props.selectedJob.id,
                    copyFrom.description,
                    copyFrom.challengeInfo,
                    source,
                );
                await props.reload();
                setPopoverTitle("Success!");
                setPopoverMsg(
                    `You have successfully cloned details from ${copyFrom.label} to ${props.selectedJob.label}. Loading the cloned result now...`,
                );
            } catch (e) {
                console.error(e);
                setPopoverTitle("Failed!");
                setPopoverMsg(`Something went wrong. Please try again.`);
            } finally {
                handleComplete();
            }
        }
    };

    useEffect(() => {
        if (props.show) {
            setCopying(false);
            setCopyTo(undefined);
            setSearch("");
            setFilters(FILTER_DEFAULT);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.show]);

    const getTitle = () => {
        if (props.action === "clone-to") {
            return `Clone details from ${props.selectedJob?.label} to...`;
        } else {
            return `Clone details to ${props.selectedJob?.label} from...`;
        }
    };

    return (
        <>
            <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter" backdrop="static" centered>
                <Modal.Header>
                    <Modal.Title id="contained-modal-title-vcenter">{getTitle()}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{pointerEvents: isCopying ? "none" : "auto"}}>
                        <JobFilters filters={filters} setFilters={setFilters} hideTitleSection={true} />
                    </div>{" "}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "5px",
                            pointerEvents: isCopying ? "none" : "auto",
                        }}
                    >
                        <InputGroup className="mb-3">
                            Search:
                            <Form.Control
                                style={{marginLeft: "5px"}}
                                placeholder="Search by Job Name or Title"
                                aria-label="Search"
                                aria-describedby="basic-addon1"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </InputGroup>
                    </div>
                    <JobList
                        jobList={props.jobList.filter((j) => j.name !== props.selectedJob.name) || []}
                        selectedJob={copyTo}
                        onClick={(j: JobDetailType) => {
                            setCopyTo(j);
                        }}
                        filters={filters}
                        search={search}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="light" disabled={isCopying} onClick={props.onHide}>
                        Close
                    </Button>
                    <Button
                        ref={target}
                        disabled={isCopying || !copyTo}
                        onClick={props.action === "clone-to" ? handleCloneTo : handleCloneFrom}
                    >
                        {isCopying ? "Cloning..." : "Clone"}
                    </Button>

                    <Overlay show={showPopover} target={target.current} placement="top" containerPadding={20}>
                        <Popover id="popover-contained">
                            <Popover.Header as="h3">{popoverTitle}</Popover.Header>
                            <Popover.Body>{popoverMsg}</Popover.Body>
                        </Popover>
                    </Overlay>
                </Modal.Footer>
            </Modal>
        </>
    );
};
